import * as React from "react";
import {
  createTheme,
  ThemeProvider,
  ThemeOptions,
  responsiveFontSizes,
} from "@mui/material/styles";

declare module "@mui/material/styles" {}

declare module "@mui/material/styles" {
  interface Palette {
    evergreen: Palette["primary"];
    fern: Palette["primary"];
    grass: Palette["primary"];
    sky: Palette["primary"];
    sun: Palette["primary"];
    snow: Palette["primary"];
    moss: Palette["primary"];
    earth: Palette["primary"];
    darkGrey: Palette["primary"];
    mediumGrey: Palette["primary"];
    lightGrey: Palette["primary"];
    headingGrey: Palette["primary"];
  }
  interface PaletteOptions {
    evergreen: PaletteOptions["primary"];
    fern: PaletteOptions["primary"];
    grass: PaletteOptions["primary"];
    sky: PaletteOptions["primary"];
    sun: PaletteOptions["primary"];
    snow: PaletteOptions["primary"];
    moss: PaletteOptions["primary"];
    earth: PaletteOptions["primary"];
    darkGrey: Palette["primary"];
    mediumGrey: Palette["primary"];
    lightGrey: Palette["primary"];
    headingGrey: Palette["primary"];
  }

  interface TypographyVariants {
    tab: React.CSSProperties;
    link: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    tab?: React.CSSProperties;
    link?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    tab: true;
    link: true;
    bodyBold: true;
  }
}

declare module "@mui/material/TextField" {
  interface TextFieldPropsColorOverrides {
    evergreen: true;
    fern: true;
    sun: true;
    grass: true;
    sky: true;
    earth: true;
    snow: true;
    moss: true;
    darkGrey: true;
    mediumGrey: true;
    lightGrey: true;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    evergreen: true;
    fern: true;
    sun: true;
    grass: true;
    sky: true;
    earth: true;
    snow: true;
    moss: true;
    darkGrey: true;
    mediumGrey: true;
    lightGrey: true;
  }
}

declare module "@mui/material/ButtonGroup" {
  interface ButtonGroupPropsColorOverrides {
    evergreen: true;
    fern: true;
    sun: true;
    grass: true;
    sky: true;
    earth: true;
    snow: true;
    moss: true;
    darkGrey: true;
    mediumGrey: true;
    lightGrey: true;
  }
}

declare module "@mui/material/Slider" {
  interface SliderPropsColorOverrides {
    evergreen: true;
    fern: true;
    sun: true;
    grass: true;
    sky: true;
    earth: true;
    snow: true;
    moss: true;
    darkGrey: true;
    mediumGrey: true;
    lightGrey: true;
  }
}

declare module "@mui/material/SvgIcon" {
  interface SvgIconPropsColorOverrides {
    evergreen: true;
    fern: true;
    sun: true;
    grass: true;
    sky: true;
    earth: true;
    snow: true;
    moss: true;
    darkGrey: true;
    mediumGrey: true;
    lightGrey: true;
  }
}

declare module "@mui/material/Tabs" {
  interface TabsPropsColorOverrides {
    sun: true;
  }
}

const defaultTheme = createTheme();

let theme = createTheme({
  typography: {
    fontFamily: [
      '"Helvetica Neue"',
      "Helvetica",
      "Arial",
      "sans-serif",
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(","),
    h1: { fontWeight: "bold", fontSize: "2.25rem" },
    h2: { fontWeight: "bold", fontSize: "1.5rem" },
    h3: {
      fontFamily: "Helvetica Neue",
      fontWeight: "bold",
      fontSize: "1.25rem",
    },
    h4: { fontFamily: "Helvetica Neue", fontSize: "1rem" },
    h5: { fontFamily: "PT Sans", fontWeight: "bold", fontSize: "1rem" },
    h6: { fontWeight: "bold", fontSize: ".75rem" },
    h7: { fontFamily: "Arial" },
    subtitle1: {
      fontFamily: "Tussilago",
      fontWeight: "bold",
      fontSize: ".875rem",
      textTransform: "uppercase",
    },
    subtitle2: {
      fontFamily: "Arial",
      fontSize: ".675rem",
    },
    body1: { fontFamily: "PT Sans", fontSize: ".875rem" },
    bodyBold: {
      fontFamily: "PT Sans",
      fontWeight: "bold",
      fontSize: ".875rem",
    },
    bodySmall: { fontFamily: "PT Sans", size: ".75rem" },
    button: {
      fontWeight: "bold",
      fontSize: ".95rem",
      style: { textTransform: "none" },
    },

    tab: {
      fontFamily: "Arial",
      fontWeight: "bold",
      fontSize: ".75rem",
      textTransform: "capitalize",
    },
    link: {
      fontFamily: "PT Sans",
      fontWeight: "bold",
      fontSize: ".875rem",
    },
    caption: {},
    overline: {},
  },
  palette: {
    evergreen: defaultTheme.palette.augmentColor({
      color: { main: "#006837", contrastText: "#F5FAF1" },
    }),
    fern: defaultTheme.palette.augmentColor({
      color: { main: "#39B54A", contrastText: "#2C2C2C" },
    }),
    grass: defaultTheme.palette.augmentColor({
      color: { main: "#8CC63F", contrastText: "#2C2C2C" },
    }),
    sky: defaultTheme.palette.augmentColor({
      color: { main: "#5DD8D8", contrastText: "#2C2C2C" },
    }),
    sun: defaultTheme.palette.augmentColor({
      color: { main: "#F7931E", contrastText: "#2C2C2C" },
    }),
    snow: defaultTheme.palette.augmentColor({
      color: { main: "#F5FAF1", contrastText: "#2C2C2C" },
    }),
    moss: defaultTheme.palette.augmentColor({
      color: { main: "#CEE4C5", contrastText: "#2C2C2C" },
    }),
    earth: defaultTheme.palette.augmentColor({
      color: { main: "#362229", contrastText: "#F5FAF1" },
    }),
    darkGrey: defaultTheme.palette.augmentColor({
      color: { main: "#333333", contrastText: "#F5FAF1" },
    }),
    mediumGrey: defaultTheme.palette.augmentColor({
      color: { main: "#5C5C5C", contrastText: "#F5FAF1" },
    }),
    lightGrey: defaultTheme.palette.augmentColor({
      color: { main: "#F1F1F1", contrastText: "#0E0E2C" },
    }),
    headingGrey: defaultTheme.palette.augmentColor({
      color: { main: "#969696", contrastText: "#F5FAF1" },
    }),
  },
  components: {
    MuiTab: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            color: "#f7931e",
            borderBottom: "4px solid #f7931e",
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: { root: { borderRadius: "0px" } },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: "bold",
          fontSize: ".75rem",
          textTransform: "none",
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: "#e7e7e7",
        },
      },
    },
  },
} as ThemeOptions);

theme = responsiveFontSizes(theme);
const ThemeContext = ({ children }: { children: React.ReactNode }) => {
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

export default ThemeContext;
